import { App } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { downloadFile } from "../../../utils/download-file.ts"
import {
  changeAttachmentStatus,
  deleteAttachment,
  permanentlyDeleteAttachment,
  renameAttachment,
  restoreAttachmentFromTrash
} from "../../Album/api/attachment.services.ts"
import { requestDocumentReview } from "../../Project/api/project-attachment-workflow.services.ts"
import {
  changeDocumentStatus,
  deleteProjectDocument,
  permanentlyDeleteProjectDocument,
  restoreProjectDocumentFromTrash,
  restoreProjectDocumentVersion,
  shareDocumentWithCustomer,
  unshareDocumentFromCustomer,
  updateProjectDocumentClassification
} from "../../Project/api/project-documents.services.ts"
import type { RelatedRecordType } from "../../Project/types/project-documents.types.ts"
import { canRequestReview } from "../../Files/helpers/document-review-status.ts"
import { readServerMessage } from "../../Files/helpers/server-message.ts"
import { useDocumentTypeOptions } from "../../Files/hooks/useDocumentTypeOptions.ts"
import { useStatusOptions } from "../../Files/hooks/useStatusOptions.ts"
import { type FileItem } from "../../Files/types/file-item.types.ts"

interface UseGlobalFileActionsArgs {
  /** Resynced-by-key snapshots (renamingFile, sharingFile, ...) are kept
   *  fresh against this list after a reload — same reasoning as
   *  `useFileActions`' own resync effect. */
  fileItems: FileItem[]
  reload: () => Promise<void> | void
  /** Absent only in tests / a caller with nowhere to navigate to. */
  onViewProject?: (file: FileItem) => void
}

// Company-scoped rows (`file.projectInfo` null) have no folder tree to move
// into and no customer to see them — Move and "Add to Album" stay
// project-only for this phase (Company Folders/Albums are future phases,
// per EAD-615 Phase B item 11's explicit scope), so this hook never exposes
// them at all, project-scoped Global row included: Global Files has no
// folder-tree UI for ANY file today, not just company-scoped ones.
export function useGlobalFileActions({
  fileItems,
  reload,
  onViewProject
}: UseGlobalFileActionsArgs) {
  const { t } = useTranslation()
  const { message } = App.useApp()

  const [renamingFile, setRenamingFile] = useState<FileItem | null>(null)
  const [assigningDocumentTypeFile, setAssigningDocumentTypeFile] = useState<FileItem | null>(
    null
  )
  const [linkingFile, setLinkingFile] = useState<FileItem | null>(null)
  const [uploadingNewVersionFile, setUploadingNewVersionFile] = useState<FileItem | null>(null)
  const [versionHistoryFile, setVersionHistoryFile] = useState<FileItem | null>(null)
  const [requestReviewFile, setRequestReviewFile] = useState<FileItem | null>(null)
  const [commentsFile, setCommentsFile] = useState<FileItem | null>(null)
  const [activityFile, setActivityFile] = useState<FileItem | null>(null)
  const [sharingFile, setSharingFile] = useState<FileItem | null>(null)

  const { options: documentTypeOptions, loadFailed: documentTypesFailed } =
    useDocumentTypeOptions()
  const { options: statusOptions, loadFailed: statusesFailed } = useStatusOptions()

  // Same reasoning as `useFileActions`: a workflow action reloads
  // `fileItems` but an already-open panel snapshot isn't derived from it, so
  // it keeps showing the pre-action row until closed and reopened.
  useEffect(() => {
    const resync = (file: FileItem | null) => {
      if (!file) return file
      const fresh = fileItems.find((item) => item.key === file.key)
      return fresh && fresh !== file ? fresh : file
    }
    setRenamingFile(resync)
    setAssigningDocumentTypeFile(resync)
    setLinkingFile(resync)
    setUploadingNewVersionFile(resync)
    setVersionHistoryFile(resync)
    setRequestReviewFile(resync)
    setCommentsFile(resync)
    setActivityFile(resync)
    setSharingFile(resync)
  }, [fileItems])

  const deleteOne = (file: FileItem) =>
    file.source === "album-attachment"
      ? deleteAttachment(file.id)
      : deleteProjectDocument(file.id)

  const changeStatusOne = (file: FileItem, value: string | null) =>
    file.source === "album-attachment"
      ? changeAttachmentStatus(file.id, value)
      : changeDocumentStatus(file.id, value)

  const changeDocumentTypeOne = (file: FileItem, value: string | null) =>
    file.source === "album-attachment"
      ? renameAttachment(file.id, { document_type: value })
      : updateProjectDocumentClassification(file.id, { document_type: value })

  const renameFile = useCallback(
    async (file: FileItem, nextName: string) => {
      // Documents (`core.ProjectAttachment`) carry no owner and have no
      // rename endpoint at all — matches `useFileActions.renameFile` exactly
      // (`canRename` is hardcoded `false` for that source, so the row menu
      // never offers this for a document either way).
      if (file.source !== "album-attachment") return
      const response = await renameAttachment(file.id, { original_filename: nextName })
      if (!response.ok) {
        const reason = readServerMessage(response.data) ?? t("files.rename.failed")
        message.error(reason)
        throw new Error(reason)
      }
      await reload()
    },
    [message, t, reload]
  )

  const updateDocumentType = useCallback(
    async (file: FileItem, value: string | null): Promise<boolean> => {
      const response = await changeDocumentTypeOne(file, value)
      if (!response.ok) {
        message.error(readServerMessage(response.data) ?? t("files.documentType.failed"))
        return false
      }
      await reload()
      return true
    },
    [message, t, reload]
  )

  const updateStatus = useCallback(
    async (file: FileItem, value: string | null): Promise<boolean> => {
      const response = await changeStatusOne(file, value)
      if (!response.ok) {
        message.error(t("files.status.failed"))
        return false
      }
      await reload()
      return true
    },
    [message, t, reload]
  )

  const updateCustomerVisibility = useCallback(
    async (file: FileItem, visible: boolean): Promise<boolean> => {
      // Same gate as `useFileActions`: only a document carries this flag.
      // A company-scoped document (`file.projectInfo` null) has no customer
      // to be visible to at all — the UI never renders this control for one
      // (see `FileDetailsPanel`'s `file.projectInfo` gate), so reaching here
      // for a company file would be a UI bug, not a case to silently allow.
      if (file.source !== "document" || !file.projectInfo) return false
      const response = visible
        ? await shareDocumentWithCustomer(file.id)
        : await unshareDocumentFromCustomer(file.id)
      if (!response.ok) {
        message.error(t("files.customerVisibility.failed"))
        return false
      }
      await reload()
      return true
    },
    [message, t, reload]
  )

  const submitRequestReview = useCallback(
    async (data: { reviewerId: string; dueDate: string | null; message: string | null }) => {
      if (!requestReviewFile) return
      const response = await requestDocumentReview({
        attachment: requestReviewFile.id,
        reviewer_id: data.reviewerId,
        due_date: data.dueDate,
        message: data.message
      })
      if (!response.ok) {
        message.error(readServerMessage(response.data) ?? t("files.review.failed"))
        return
      }
      message.success(t("files.review.succeeded"))
      setRequestReviewFile(null)
      await reload()
    },
    [requestReviewFile, message, t, reload]
  )

  const linkRecord = useCallback(
    async (recordType: RelatedRecordType, recordId: string) => {
      if (!linkingFile || linkingFile.source !== "document") return
      const response = await updateProjectDocumentClassification(linkingFile.id, {
        related_record_type: recordType,
        related_record_id: recordId
      })
      if (!response.ok) {
        message.error(readServerMessage(response.data) ?? t("files.linkRecord.failed"))
        return
      }
      message.success(t("files.linkRecord.succeeded"))
      setLinkingFile(null)
      await reload()
    },
    [linkingFile, message, t, reload]
  )

  const unlinkRecord = useCallback(async () => {
    if (!linkingFile || linkingFile.source !== "document") return
    const response = await updateProjectDocumentClassification(linkingFile.id, {
      related_record_type: null,
      related_record_id: null
    })
    if (!response.ok) {
      message.error(readServerMessage(response.data) ?? t("files.linkRecord.failed"))
      return
    }
    message.success(t("files.linkRecord.unlinked"))
    setLinkingFile(null)
    await reload()
  }, [linkingFile, message, t, reload])

  const restoreVersion = useCallback(
    async (versionId: string) => {
      const response = await restoreProjectDocumentVersion(versionId)
      if (!response.ok || !response.data) {
        message.error(
          readServerMessage(response.data) ?? t("files.versionHistory.restoreFailed")
        )
        return null
      }
      message.success(t("files.versionHistory.restoreSucceeded"))
      setVersionHistoryFile(null)
      await reload()
      return response.data.details.data
    },
    [message, t, reload]
  )

  const deleteFile = useCallback(
    async (file: FileItem) => {
      const response = await deleteOne(file)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ?? t("files.delete.failed", { name: file.name })
        )
        return false
      }
      message.success(t("files.delete.succeeded", { name: file.name }))
      await reload()
      return true
    },
    [message, t, reload]
  )

  // No Global Files surface lists trashed rows today (there is no Global
  // Trash nav, unlike the per-project Files sidebar) — these two exist for
  // parity with `useFileActions` and for a future Global Trash tab to call,
  // but nothing in this phase's 3 wired surfaces can reach them yet. Both
  // are plain attachment/document-id calls, same as their per-project
  // counterparts, so nothing about "no project id" changes their shape.
  const restoreFile = useCallback(
    async (file: FileItem) => {
      const response =
        file.source === "album-attachment"
          ? await restoreAttachmentFromTrash(file.id)
          : await restoreProjectDocumentFromTrash(file.id)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ?? t("files.restore.failed", { name: file.name })
        )
        return false
      }
      message.success(t("files.restore.succeeded", { name: file.name }))
      await reload()
      return true
    },
    [message, t, reload]
  )

  const permanentlyDeleteFile = useCallback(
    async (file: FileItem) => {
      const response =
        file.source === "album-attachment"
          ? await permanentlyDeleteAttachment(file.id)
          : await permanentlyDeleteProjectDocument(file.id)
      if (!response.ok) {
        message.error(
          readServerMessage(response.data) ??
            t("files.permanentDelete.failed", { name: file.name })
        )
        return false
      }
      message.success(t("files.permanentDelete.succeeded", { name: file.name }))
      await reload()
      return true
    },
    [message, t, reload]
  )

  const fileActions = useCallback(
    (file: FileItem): MenuItemType[] => {
      const items: MenuItemType[] = [
        {
          key: "download",
          label: t("files.actions.download"),
          onClick: () => void downloadFile(file.fileUrl, file.name)
        }
      ]
      if (file.projectInfo && onViewProject) {
        items.push({
          key: "open-project",
          label: file.projectInfo.is_project
            ? t("globalFiles.actions.openProject", "Open project")
            : t("globalFiles.actions.openLead", "Open lead"),
          onClick: () => onViewProject(file)
        })
      }
      if (file.canRename) {
        items.push({
          key: "rename",
          label: t("files.actions.rename"),
          onClick: () => setRenamingFile(file)
        })
      }
      if (file.canMove) {
        items.push({
          key: "assignDocumentType",
          label: t("files.actions.assignDocumentType"),
          onClick: () => setAssigningDocumentTypeFile(file)
        })
      }
      if (file.source === "document" && file.version !== undefined) {
        if (file.canMove && file.isCurrentVersion !== false) {
          items.push({
            key: "uploadNewVersion",
            label: t("files.uploadNewVersion.action"),
            onClick: () => setUploadingNewVersionFile(file)
          })
        }
        items.push({
          key: "versionHistory",
          label: t("files.versionHistory.action"),
          onClick: () => setVersionHistoryFile(file)
        })
      }
      if (file.canShare) {
        items.push({
          key: "share",
          label: t("files.actions.share"),
          onClick: () => setSharingFile(file)
        })
      }
      if (file.source === "document") {
        // "View Review" (approve/reject/request changes) is deliberately
        // left out here — that workflow already has a dedicated cross-project
        // surface (the Document Review workspace behind Approval ▸ My
        // Approvals / Sent for Review), so Global Files only needs to be
        // able to kick a review off, not re-implement approving one.
        if (file.canMove && canRequestReview(file.status)) {
          items.push({
            key: "requestReview",
            label: t("files.review.requestAction"),
            onClick: () => setRequestReviewFile(file)
          })
        }
        items.push({
          key: "comments",
          label: t("files.comments.viewAction"),
          onClick: () => setCommentsFile(file)
        })
        items.push({
          key: "activity",
          label: t("files.activity.viewAction"),
          onClick: () => setActivityFile(file)
        })
      }
      if (file.canDelete) {
        items.push({
          key: "delete",
          danger: true,
          label: t("files.actions.moveToTrash"),
          onClick: () => void deleteFile(file)
        })
      }
      return items
    },
    [t, onViewProject, deleteFile]
  )

  return {
    documentTypeOptions,
    documentTypesFailed,
    statusOptions,
    statusesFailed,
    renamingFile,
    setRenamingFile,
    renameFile,
    assigningDocumentTypeFile,
    setAssigningDocumentTypeFile,
    updateDocumentType,
    updateStatus,
    updateCustomerVisibility,
    sharingFile,
    setSharingFile,
    linkingFile,
    setLinkingFile,
    linkRecord,
    unlinkRecord,
    uploadingNewVersionFile,
    setUploadingNewVersionFile,
    versionHistoryFile,
    setVersionHistoryFile,
    restoreVersion,
    requestReviewFile,
    setRequestReviewFile,
    submitRequestReview,
    commentsFile,
    setCommentsFile,
    activityFile,
    setActivityFile,
    deleteFile,
    restoreFile,
    permanentlyDeleteFile,
    fileActions
  }
}
